import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { useCreateTeam, useUpdateTeam } from '../hooks'
import type { Team } from '../types'
import { formatError } from './formatError'

const CODE_REGEX = /^[a-z][a-z0-9_]{0,63}$/

export function TeamFormDialog({ editing, allTeams, onClose }: {
  editing: Team | null
  allTeams: Team[]
  onClose: () => void
}) {
  const create = useCreateTeam()
  const update = useUpdateTeam()
  const [code, setCode] = useState(editing?.code ?? '')
  const [name, setName] = useState(editing?.name ?? '')
  const [parentId, setParentId] = useState<number | null>(editing?.parentId ?? null)
  const [enablesAvailability, setEnablesAvailability] = useState(editing?.enablesAvailability ?? false)
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    if (!editing && !CODE_REGEX.test(code)) {
      setError('Kod: małe litery, cyfry, podkreślenia. Musi zaczynać się od litery.')
      return
    }
    if (!name.trim()) { setError('Nazwa nie może być pusta.'); return }
    if (editing && parentId === editing.id) { setError('Zespół nie może być rodzicem samego siebie.'); return }

    if (editing) {
      update.mutate(
        { id: editing.id, payload: { name, parentId, enablesAvailability } },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    } else {
      create.mutate(
        { code, name, parentId, enablesAvailability },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    }
  }

  const isSubmitting = create.isPending || update.isPending
  const parentOptions = allTeams.filter(t => t.id !== editing?.id)

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[440px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">
            {editing ? `Edycja zespołu ${editing.code}` : 'Nowy zespół'}
          </Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            Zespół to jednostka organizacyjna - może być regionalna (Poznań-Sprzedaż),
            funkcjonalna (Centralna-Księgowość) lub hybrydowa.
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Kod</span>
              <input
                value={code}
                onChange={e => setCode(e.target.value.toLowerCase())}
                disabled={!!editing || isSubmitting}
                className="border rounded px-2 py-1 font-mono"
                placeholder="np. poznan_sprzedaz"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Nazwa</span>
              <input value={name} onChange={e => setName(e.target.value)} disabled={isSubmitting} className="border rounded px-2 py-1" />
            </label>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Zespół-rodzic</span>
              <Combobox<number>
                value={parentId}
                onChange={v => setParentId(v)}
                options={parentOptions.map(t => ({ value: t.id, label: `${t.name} (${t.code})` }))}
                emptyOptionLabel="— brak (zespół-korzeń) —"
                placeholder="— brak (zespół-korzeń) —"
                disabled={isSubmitting}
              />
              <span className="text-[10px] text-slate-400">
                Członkowie zespołu-rodzica widzą umowy ze wszystkich pod-zespołów.
              </span>
            </label>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enablesAvailability}
                onChange={e => setEnablesAvailability(e.target.checked)}
                disabled={isSubmitting}
                className="w-4 h-4 mt-0.5"
              />
              <span className="flex flex-col gap-0.5">
                <span>Włącz status gotowości pracowników</span>
                <span className="text-[10px] text-slate-400">
                  Po włączeniu członkowie zespołu zobaczą na pasku u góry widget
                  do oznaczania dostępności (Gotowy / Zajęty / Niedostępny).
                  Używane do auto-przypisywania spraw (np. zespół Kredyty).
                </span>
              </span>
            </label>

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={isSubmitting}>Anuluj</button>
              <button type="button" onClick={submit} className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50" disabled={isSubmitting}>
                {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
